/* BOJOURNAL — wall-finder.js
   <wall-finder>: the size advisor. From what the work hangs above and how
   wide it is, it suggests a width of 62–78% (the "two-thirds to three-
   quarters" rule), draws it on a wall, lists the works that fit by their
   real size (and budget), and prepares a message to the gallery. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('wall-finder')) return;

  const NS = 'http://www.w3.org/2000/svg';
  const RATIO_MIN = 0.62;
  const RATIO_MAX = 0.78;
  const TOLERANCE = 0.08;

  const el = (name, attrs) => {
    const node = document.createElementNS(NS, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  const esc = (text) => String(text || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const round5 = (n) => Math.round(n / 5) * 5;

  class WallFinder extends B.Component {
    init() {
      try {
        this.works = JSON.parse(this.querySelector('[data-finder-works]').textContent) || [];
      } catch (error) {
        this.works = [];
      }
      this.range = this.querySelector('[data-width-range]');
      this.input = this.querySelector('[data-width-input]');
      this.scene = this.querySelector('[data-scene]');
      this.list = this.querySelector('[data-works]');
      this.status = this.querySelector('[data-status]');
      this.ideal = this.querySelector('[data-ideal]');
      this.ask = this.querySelector('[data-ask]');

      this.on(this, 'change', (event) => {
        if (event.target.matches('input[type="radio"][data-default-width]')) {
          this.setWidth(parseInt(event.target.dataset.defaultWidth, 10));
        }
        this.update();
      });
      this.on(this.range, 'input', () => {
        this.input.value = this.range.value;
        this.update();
      });
      this.on(this.input, 'input', () => {
        const value = parseInt(this.input.value, 10);
        if (value >= 40 && value <= 800) {
          this.range.value = Math.min(450, Math.max(60, value));
          this.update();
        }
      });
      this.update();
    }

    setWidth(value) {
      this.input.value = value;
      this.range.value = value;
    }

    get kind() {
      const checked = this.querySelector('input[data-default-width]:checked');
      return checked ? { value: checked.value, name: checked.dataset.name } : { value: 'sofa', name: '' };
    }

    get budget() {
      const checked = this.querySelector('input[name^="finder-budget"]:checked');
      return checked ? { value: checked.value, name: checked.dataset.name } : { value: 'any', name: '' };
    }

    inBudget(work, budget) {
      const s1 = parseInt(this.dataset.step1, 10);
      const s2 = parseInt(this.dataset.step2, 10);
      if (budget === '1') return work.c <= s1;
      if (budget === '2') return work.c > s1 && work.c <= s2;
      if (budget === '3') return work.c > s2;
      return true;
    }

    update() {
      const width = Math.max(40, Math.min(800, parseInt(this.input.value, 10) || 0));
      const min = round5(width * RATIO_MIN);
      const max = round5(width * RATIO_MAX);
      const mid = (min + max) / 2;
      const budget = this.budget;
      const kind = this.kind;

      this.ideal.innerHTML = '<span dir="ltr">' + min + '–' + max + '</span> ' + esc(this.dataset.unit);

      const pool = this.works.filter((work) => this.inBudget(work, budget.value));
      let fits = pool.filter((work) => work.w >= min * (1 - TOLERANCE) && work.w <= max * (1 + TOLERANCE));
      let exact = true;
      if (!fits.length) {
        exact = false;
        fits = pool.slice();
      }
      fits.sort((a, b) => Math.abs(a.w - mid) - Math.abs(b.w - mid));
      const shown = fits.slice(0, exact ? 6 : 3);

      if (exact) {
        this.status.textContent = shown.length === 1 && fits.length === 1 ? this.dataset.fitOne : this.dataset.fit.replace('[n]', fits.length);
      } else {
        this.status.textContent = shown.length ? this.dataset.closest : this.dataset.none || '';
      }
      this.list.innerHTML = shown
        .map(
          (work) =>
            '<li><a class="finder-work" href="' + esc(work.u) + '">' +
            '<span class="finder-work__media">' + (work.i ? '<img src="' + esc(work.i) + '" alt="" loading="lazy" width="240" height="240">' : '') + '</span>' +
            '<span class="finder-work__title">' + esc(work.t) + '</span>' +
            '<span class="finder-work__size t-caption t-muted t-tabular"><span dir="ltr">' + Math.round(work.w) + ' × ' + Math.round(work.h) + '</span> ' + esc(this.dataset.unit) + '</span>' +
            '<span class="finder-work__price t-small">' + work.p + '</span>' +
            '</a></li>'
        )
        .join('');

      this.draw(kind.value, width, min, max, exact && shown[0] ? shown[0] : null);

      // The message to the gallery.
      const budgetText = budget.value !== 'any' && budget.name ? (this.dataset.msgBudget || '').replace('[budget]', budget.name) : '';
      const message = (this.dataset.msg || '')
        .replace('[kind]', kind.name)
        .replace('[width]', width)
        .replace('[range]', min + '–' + max)
        .replace('[budget]', budgetText);
      if (this.dataset.wa) {
        this.ask.href = 'https://wa.me/' + this.dataset.wa + '?text=' + encodeURIComponent(message);
      } else {
        const contact = this.dataset.contact || '/pages/contact';
        this.ask.href = contact + (contact.includes('?') ? '&' : '?') + 'contact%5Bbody%5D=' + encodeURIComponent(message);
      }
    }

    /* The wall: furniture at its width, the ideal range above it, and —
       when one fits — the first matching work drawn at its real size.
       Heights are measured from the floor, in centimetres. */
    draw(kind, width, min, max, work) {
      const furnitureW = kind === 'wall' ? 0 : width;
      const topH = { sofa: 84, bed: 110, console: 80 }[kind] || 0;
      const zoneH = work ? work.h : Math.min(max * 0.7, 160);
      const bottomH = kind === 'wall' ? Math.max(30, 150 - zoneH / 2) : topH + 22;
      const wallH = Math.max(260, bottomH + zoneH + 30);
      const floor = wallH;
      // Wide enough to fill the panel, so the wall and floor run edge to edge.
      const viewW = Math.max(kind === 'wall' ? width + 60 : width * 1.5, max + 80, (floor + 14) * 1.8);
      const cx = viewW / 2;
      const svg = el('svg', { viewBox: '0 0 ' + viewW.toFixed(1) + ' ' + (floor + 14).toFixed(1), class: 'finder__svg', preserveAspectRatio: 'xMidYMax meet' });
      svg.appendChild(el('rect', { x: 0, y: 0, width: viewW, height: floor, class: 'finder__wall' }));
      svg.appendChild(el('rect', { x: 0, y: floor, width: viewW, height: 14, class: 'finder__floor' }));

      const x0 = cx - furnitureW / 2;
      if (kind === 'sofa') {
        svg.appendChild(el('rect', { x: x0 + 8, y: floor - 84, width: furnitureW - 16, height: 46, rx: 9, class: 'finder__piece finder__piece--back' }));
        svg.appendChild(el('rect', { x: x0, y: floor - 50, width: furnitureW, height: 32, rx: 7, class: 'finder__piece' }));
        svg.appendChild(el('rect', { x: x0 - 2, y: floor - 62, width: 22, height: 44, rx: 8, class: 'finder__piece finder__piece--arm' }));
        svg.appendChild(el('rect', { x: x0 + furnitureW - 20, y: floor - 62, width: 22, height: 44, rx: 8, class: 'finder__piece finder__piece--arm' }));
        svg.appendChild(el('rect', { x: x0 + 14, y: floor - 18, width: 4, height: 18, class: 'finder__leg' }));
        svg.appendChild(el('rect', { x: x0 + furnitureW - 18, y: floor - 18, width: 4, height: 18, class: 'finder__leg' }));
      } else if (kind === 'bed') {
        svg.appendChild(el('rect', { x: x0, y: floor - 110, width: furnitureW, height: 110, rx: 6, class: 'finder__piece finder__piece--back' }));
        svg.appendChild(el('rect', { x: x0 - 6, y: floor - 52, width: furnitureW + 12, height: 40, rx: 6, class: 'finder__piece' }));
        svg.appendChild(el('rect', { x: x0 + 10, y: floor - 64, width: furnitureW * 0.4, height: 14, rx: 7, class: 'finder__piece finder__piece--arm' }));
        svg.appendChild(el('rect', { x: x0 + furnitureW * 0.6 - 10, y: floor - 64, width: furnitureW * 0.4, height: 14, rx: 7, class: 'finder__piece finder__piece--arm' }));
      } else if (kind === 'console') {
        svg.appendChild(el('rect', { x: x0, y: floor - 80, width: furnitureW, height: 5, class: 'finder__leg' }));
        svg.appendChild(el('rect', { x: x0 + 6, y: floor - 75, width: 3, height: 75, class: 'finder__leg' }));
        svg.appendChild(el('rect', { x: x0 + furnitureW - 9, y: floor - 75, width: 3, height: 75, class: 'finder__leg' }));
      }

      // The ideal range: the widest outline dashed, the narrowest shaded.
      const zoneTop = floor - bottomH - zoneH;
      svg.appendChild(el('rect', { x: cx - min / 2, y: zoneTop, width: min, height: zoneH, class: 'finder__zone' }));
      if (work) {
        svg.appendChild(el('rect', { x: cx - work.w / 2 + 1.5, y: zoneTop + 3, width: work.w, height: work.h, class: 'finder__shadow' }));
        svg.appendChild(el('rect', { x: cx - work.w / 2, y: zoneTop, width: work.w, height: work.h, class: 'finder__work' }));
      }
      svg.appendChild(el('rect', { x: cx - max / 2, y: zoneTop, width: max, height: zoneH, class: 'finder__zone-line' }));

      this.scene.replaceChildren(svg);
    }
  }

  B.define('wall-finder', WallFinder);
})();
